/* eslint-disable camelcase */
import { Grid } from '@mui/material';
import { pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalSelect, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import HideIcon from '../../../../assets/svg/HideIcon';
import More3Points from '../../../../assets/svg/More3Points';
import ShowIcon from '../../../../assets/svg/ShowIcon';
import { HeaderContent } from '../../../../components/HeaderContent';
import { ApiKey } from '../../../../domain/apiKey/ApiKey';
import { useRetrieveApiKeys } from '../../../../hooks/apiKey/useRetrieveApiKeys';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveSupportDocs } from '../../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { DataTable } from '../../../../react-components/data-table/DataTable';
import { UpgradeIntegrations } from '../common/UpgradeIntegrations';
import { CreateApiKeyDialog } from './dialogs/CreateApiKeyDialog';
import { DeleteApiKeyDialog } from './dialogs/DeleteApiKeyDialog';
import { DisableApiKeyDialog } from './dialogs/DisableApiKeyDialog';
import { EnableApiKeyDialog } from './dialogs/EnableApiKeyDialog';
import { UpdateApiKeyDialog } from './dialogs/UpdateApiKeyDialog';

const DELAY_COPY_INFO = 2000;

export const ApiKeys = () => {
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: apiKeys, run: retrieveApiKeys } = useRetrieveApiKeys();
  const { data: organization } = useRetrieveOrganization();

  const [activeDialog, setActiveDialog] = useState<null | `create` | `update` | `disable` | `enable` | `delete`>(null);
  const [activeApiKey, setActiveApiKey] = useState<ApiKey>();
  const [visibleApiKey, setVisibleApiKey] = useState<Record<string, boolean>>({});
  const [copyTooltipVisible, setCopyTooltipVisible] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (apiKeys) {
      const resetApiKeyRecords = apiKeys.reduce(
        (prev, apiKey) => ({ ...prev, [apiKey.id as string]: false }),
        {} as Record<string, boolean>
      );

      setVisibleApiKey(resetApiKeyRecords);
      setCopyTooltipVisible(resetApiKeyRecords);
    }
  }, [apiKeys]);

  const handleCopyApiKey = (apiKey: ApiKey) => {
    const code = apiKey.key;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code);
    } else {
      const el = document.createElement(`textarea`);
      el.value = code;
      const helper = document.getElementById(`helper_copy_assign`);
      if (!helper) return;

      helper.appendChild(el);
      el.select();
      document.execCommand(`copy`);
      helper.removeChild(el);
    }

    setCopyTooltipVisible({ ...copyTooltipVisible, [apiKey.id]: true });
    setTimeout(() => {
      setCopyTooltipVisible({ ...copyTooltipVisible, [apiKey.id]: false });
    }, DELAY_COPY_INFO);
  };

  const closeApiKeyDialog = () => {
    setActiveDialog(null);
    retrieveApiKeys();
  };

  if (!userCredentials?.subscription.test(`planner.api`)) {
    return <UpgradeIntegrations />;
  }

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ height: `100%`, backgroundColor: `white`, overflowY: `auto` }}
    >
      <Grid container direction="column" wrap="nowrap">
        <Grid
          container
          direction="row"
          wrap="nowrap"
          style={{
            padding: `11px 20px`,
            backgroundColor: RoutalPalette.neutral02n,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
        >
          <HeaderContent
            titleIntlId="settings.developers.api_keys.title"
            titleIntlValues={{ organizationName: organization?.label ?? `` }}
            styles={{
              container: {
                display: `flex`,
                flexDirection: `column`,
                justifyContent: `center`,
              },
            }}
          />

          <Grid container direction="row" wrap="nowrap" justifyContent="flex-end">
            <Button
              style={{ fontWeight: `400` }}
              styleType="secondary"
              intlId="settings.developers.api_keys.visit_developers_site"
              onClick={() =>
                window.open(retrieveDoc({ supportPage: `developers`, language: user?.language.value }), `_blank`)
              }
            />
          </Grid>
        </Grid>

        <VSpacer />

        <Grid container direction="column" padding="32px 24px">
          <DataTable
            emptyMessage={<Text intlId="settings.developers.api_keys.no_api_keys" />}
            docs={(apiKeys ?? [])
              ?.sort((ak1: ApiKey, ak2: ApiKey) => (ak1.created_at! < ak2.created_at! ? 1 : -1))
              .map((apiKey: ApiKey) => {
                return {
                  doc: apiKey,
                };
              })}
            showCheckboxes={false}
            total={apiKeys?.length ?? 0}
            columns={[
              {
                accessorName: `name`,
                columnStyle: { width: `auto !important`, flex: 2, overflow: `hidden` },
                name: <Text variant="label" intlId="settings.developers.api_keys.column.name" />,
                formatter: ({ doc }: { doc: ApiKey }) => {
                  return (
                    <RoutalTooltip
                      title={<Text style={{ color: RoutalPalette.neutral00 }}>{doc.label}</Text>}
                      backgroundColor={RoutalPalette.secondary20}
                    >
                      <Text style={{ opacity: doc.active ? 1 : 0.4, overflow: `hidden`, textOverflow: `ellipsis` }}>
                        {doc.label}
                      </Text>
                      {` `}
                    </RoutalTooltip>
                  );
                },
              },
              {
                accessorName: `key`,
                columnStyle: { width: `auto !important`, flex: 4, justifyContent: `center` },
                valueCellStyle: {
                  display: `flex`,
                  justifyContent: `space-between`,
                  width: `100%`,
                  flexDirection: `column`,
                },
                align: `center`,
                name: <Text variant="label" intlId="settings.developers.api_keys.column.key" />,
                formatter: ({ doc }: { doc: ApiKey }) => {
                  const isVisible = visibleApiKey[doc.id as string];
                  return (
                    <Grid
                      style={{
                        display: `flex`,
                        flexDirection: `row`,
                        alignItems: `center`,
                        justifyContent: `space-between`,
                        opacity: doc.active ? 1 : 0.4,
                      }}
                    >
                      <Grid
                        onClick={() => {
                          handleCopyApiKey(doc);
                        }}
                        style={{ border: `none`, marginRight: `8px`, cursor: `pointer` }}
                      >
                        <RoutalTooltip
                          title={
                            <Text
                              intlId="settings.developers.api_keys.column.key.copied"
                              style={{ color: RoutalPalette.neutral00 }}
                            />
                          }
                          backgroundColor={RoutalPalette.secondary20}
                          visible={copyTooltipVisible[doc.id]}
                          mouseLeaveDelay={DELAY_COPY_INFO}
                        >
                          {isVisible ? (
                            <Text
                              style={{
                                fontSize: `14px`,
                                textAlign: `center`,
                              }}
                            >
                              {doc.key}
                            </Text>
                          ) : (
                            <Text style={{ paddingTop: `7px` }}>
                              ********************************************************
                            </Text>
                          )}
                        </RoutalTooltip>
                      </Grid>
                      <Grid
                        onClick={() => {
                          setVisibleApiKey((prevValue) => ({ ...prevValue, [doc.id as string]: !isVisible }));
                        }}
                        style={{ border: `none`, cursor: `pointer` }}
                      >
                        {isVisible ? (
                          <ShowIcon width="20px" height="20px" />
                        ) : (
                          <HideIcon width="20px" height="12px" style={{ paddingTop: 8 }} />
                        )}
                      </Grid>
                    </Grid>
                  );
                },
              },
              {
                accessorName: `enabled`,
                columnStyle: { width: `auto !important`, flex: 2, justifyContent: `center` },
                name: <Text variant="label" intlId="settings.developers.api_keys.column.status" />,
                formatter: ({ doc }: { doc: ApiKey }) =>
                  !doc.active && (
                    <Text
                      style={{ opacity: doc.active ? 1 : 0.4 }}
                      intlId="settings.developers.api_keys.column.disabled"
                    />
                  ),
              },
              {
                accessorName: `created_at`,
                columnStyle: { width: `auto !important`, flex: 2, justifyContent: `center` },
                name: <Text variant="label" intlId="settings.developers.api_keys.column.creation_date" />,
                formatter: ({ doc }: { doc: ApiKey }) => (
                  <Text style={{ opacity: doc.active ? 1 : 0.4 }}>{pastDateTime(doc.created_at)}</Text>
                ),
              },
              {
                accessorName: `actions`,
                align: `right`,
                columnStyle: { width: `auto !important`, flex: 1, justifyContent: `center` },
                formatter: ({ doc }: { doc: ApiKey }) => {
                  return (
                    <RoutalSelect
                      id="project-apikey-actions"
                      hideArrow
                      style={{
                        width: `28px`,
                        height: `28px`,
                        padding: `2px`,
                        margin: `0px`,
                        border: `none`,
                        backgroundColor: `transparent`,
                      }}
                      popoverProps={{
                        anchor: {
                          anchorOrigin: {
                            vertical: `bottom`,
                            horizontal: `right`,
                          },
                          transformOrigin: {
                            vertical: `top`,
                            horizontal: `right`,
                          },
                        },
                        options: [
                          {
                            key: `edit`,
                            onClick: () => {
                              setActiveApiKey(doc);
                              setActiveDialog(`update`);
                            },
                            content: (
                              <Text intlId="settings.developers.api_keys.actions.edit" style={{ cursor: `pointer` }} />
                            ),
                          },
                          {
                            key: `disable`,
                            onClick: () => {
                              setActiveApiKey(doc);
                              if (doc.active) {
                                setActiveDialog(`disable`);
                              } else {
                                setActiveDialog(`enable`);
                              }
                            },
                            content: (
                              <Text
                                intlId={
                                  doc.active
                                    ? `settings.developers.api_keys.actions.disable`
                                    : `settings.developers.api_keys.actions.enable`
                                }
                                style={{ cursor: `pointer` }}
                              />
                            ),
                          },
                          {
                            key: `delete`,
                            onClick: () => {
                              setActiveApiKey(doc);
                              setActiveDialog(`delete`);
                            },
                            listItemStyles: {
                              borderTop: `1px solid ${RoutalPalette.neutral10}`,
                              color: RoutalPalette.error.medium,
                            },
                            content: (
                              <Text
                                intlId="settings.developers.api_keys.actions.delete"
                                style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                              />
                            ),
                          },
                        ],
                      }}
                    >
                      <More3Points />
                    </RoutalSelect>
                  );
                },
              },
            ]}
          />
        </Grid>

        <VSpacer small />

        <Grid container direction="row" justifyContent="flex-end" padding="0px 24px">
          <Button
            onClick={() => {
              setActiveDialog(`create`);
            }}
          >
            <Text
              intlId="settings.developers.api_keys.create"
              variant="body"
              style={{ color: RoutalPalette.neutral00 }}
            />
          </Button>
        </Grid>
      </Grid>

      {activeDialog && (
        <>
          {activeDialog === `create` && <CreateApiKeyDialog onClose={closeApiKeyDialog} />}
          {activeDialog === `update` && activeApiKey && (
            <UpdateApiKeyDialog apiKey={activeApiKey} onClose={closeApiKeyDialog} />
          )}
          {activeDialog === `disable` && activeApiKey && (
            <DisableApiKeyDialog apiKey={activeApiKey} onClose={closeApiKeyDialog} />
          )}
          {activeDialog === `enable` && activeApiKey && (
            <EnableApiKeyDialog apiKey={activeApiKey} onClose={closeApiKeyDialog} />
          )}
          {activeDialog === `delete` && activeApiKey && (
            <DeleteApiKeyDialog apiKey={activeApiKey} onClose={closeApiKeyDialog} />
          )}
        </>
      )}
    </Grid>
  );
};
